import { useCallback, useEffect, useState } from 'react';
import { fetchArchiveManager, ApiError } from '@/lib/api.js';

const FIRST_GW = 1;
const LAST_GW = 38;

/*
 * Archived season for one manager.
 *
 * Differs from useManager in that the gameweek is resolved server-side:
 * /api/archive returns the whole payload for the requested gw, so
 * navigation is a re-fetch rather than a patch. The season-level stats and
 * charts are identical across gameweeks, so only the pitch visibly changes.
 */
export function useArchiveManager({ season, managerCode }) {
	const [data, setData] = useState(null);
	const [picks, setPicks] = useState({ playing: null, bench: null });
	const [status, setStatus] = useState('idle');
	const [navigating, setNavigating] = useState(false);
	const [error, setError] = useState(null);

	const load = useCallback(
		async (gw, { isNavigation = false } = {}) => {
			if (!season || !managerCode) return;
			if (isNavigation) setNavigating(true);
			else setStatus('loading');
			setError(null);

			try {
				const payload = await fetchArchiveManager({ season, managerCode, gw });
				if (!payload) {
					setError('No archived season found for that manager.');
					setStatus('error');
					return;
				}
				setData(payload);
				setPicks({
					playing: payload.playing_picks,
					bench: payload.bench_picks,
				});
				setStatus('ready');
			} catch (err) {
				setError(
					err instanceof ApiError ? err.message : 'Could not load that season.',
				);
				setStatus('error');
			} finally {
				setNavigating(false);
			}
		},
		[season, managerCode],
	);

	useEffect(() => {
		load();
	}, [load]);

	const goToGameweek = useCallback(
		(direction) => {
			if (!data?.gw) return;
			const target = direction === 'prev' ? data.gw - 1 : data.gw + 1;
			if (target < FIRST_GW || target > LAST_GW) return;
			load(target, { isNavigation: true });
		},
		[data, load],
	);

	return {
		data,
		picks,
		status,
		error,
		navigating,
		goToGameweek,
		canGoPrev: Boolean(data?.gw) && data.gw > FIRST_GW,
		canGoNext: Boolean(data?.gw) && data.gw < LAST_GW,
		showPoints: data?.points !== undefined && data?.points !== null,
	};
}
